import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

interface IdleTimeoutWarningProps {
  open: boolean;
  timeRemaining: number; // in milliseconds
  onExtendSession: () => void;
  onTimeout: () => void;
}

/**
 * Warning dialog shown 2 minutes before idle timeout
 * Displays countdown and allows user to extend session
 */
export const IdleTimeoutWarning: React.FC<IdleTimeoutWarningProps> = ({
  open,
  timeRemaining,
  onExtendSession,
  onTimeout,
}) => {
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(timeRemaining / 1000));
  const startTimeRef = useRef<number>(Date.now());
  const initialTimeRef = useRef<number>(timeRemaining);

  // Update when timeRemaining prop changes (when dialog opens)
  useEffect(() => {
    if (open) {
      initialTimeRef.current = timeRemaining;
      startTimeRef.current = Date.now();
      setSecondsRemaining(Math.ceil(timeRemaining / 1000));
    }
  }, [open, timeRemaining]);

  // Countdown timer
  useEffect(() => {
    if (!open) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, initialTimeRef.current - elapsed);
      const newSeconds = Math.ceil(remaining / 1000);
      setSecondsRemaining(newSeconds);

      if (newSeconds <= 0) {
        clearInterval(interval);
        onTimeout();
      }
    }, 100); // Update every 100ms for smoother countdown

    return () => clearInterval(interval);
  }, [open, onTimeout]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const initialSeconds = Math.ceil(initialTimeRef.current / 1000);
  const progress = initialSeconds > 0 ? ((initialSeconds - secondsRemaining) / initialSeconds) * 100 : 0;

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" sx={{ fontSize: 28 }} />
          <Typography variant="h6" component="span">
            Session Timeout Warning
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Your session will expire due to inactivity in:
          </Typography>
          <Box sx={{ my: 3, textAlign: 'center' }}>
            <Typography
              variant="h3"
              component="div"
              color="primary"
              fontWeight="bold"
              sx={{ mb: 2 }}
            >
              {formatTime(secondsRemaining)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                },
              }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary">
            Click "Stay Logged In" to continue your session, or you will be automatically logged out.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onExtendSession}
          size="large"
          sx={{ minWidth: 150 }}
        >
          Stay Logged In
        </Button>
      </DialogActions>
    </Dialog>
  );
};
